import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import {
  fetchStringsItems,
  getCachedStringsItems,
  clearStringsCache,
  stringsStore,
  StringsItem
} from "@/lib/stores/stringsStore";

interface UseStringsDataOptions {
  gameId?: string; // Optional: if not provided, uses selected game
  forceReload?: boolean;
  autoLoad?: boolean;
}

interface UseStringsDataReturn {
  stringsItems: StringsItem[];
  isLoading: boolean;
  loadData: (forceReload?: boolean) => Promise<void>;
  clearCache: () => Promise<void>;
  getCachedData: () => StringsItem[];
}

export function useStringsData({
  gameId,
  forceReload = false,
  autoLoad = true,
}: UseStringsDataOptions = {}): UseStringsDataReturn {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [stringsItems, setStringsItems] = useState<StringsItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load strings data
  const loadData = async (forceReloadData = false) => {
    if (!gameId) {
      console.warn("No game ID available for loading strings items");
      return;
    }

    setIsLoading(true);
    try {
      // Fetch strings items from the API
      const fetchedItems = await fetchStringsItems(gameId, toast);
      setStringsItems(fetchedItems);
    } catch (error) {
      console.error("Error loading strings items:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear cache
  const clearCache = async () => {
    try {
      // Clear the store data
      await clearStringsCache();
      setStringsItems([]);
      toast({
        title: "Cache cleared",
        description: "Strings cache cleared",
      });
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  };

  // Get cached data without loading
  const getCachedData = (): StringsItem[] => {
    if (!gameId) return [];
    return getCachedStringsItems(gameId);
  };

  // Auto-load on mount only if we have a game ID
  useEffect(() => {
    if (autoLoad && gameId) {
      loadData(forceReload);
    }
  }, [gameId, autoLoad, forceReload]);

  // Subscribe to store changes for real-time updates only if we have a game ID
  useEffect(() => {
    if (!gameId) return;
    
    const unsubscribe = stringsStore.subscribe(() => {
      const cachedItems = getCachedStringsItems(gameId);
      setStringsItems(cachedItems);
    });

    return () => {
      unsubscribe();
    };
  }, [gameId]);

  return {
    stringsItems,
    isLoading,
    loadData,
    clearCache,
    getCachedData,
  };
} 